import {
  webCameraIsAvailable,
  webScanImage,
  webScanOnce,
  webScannerIsAvailable,
  type PlatformScanOnceOptions,
  type PlatformScanResult,
} from "@/platform/barcode";

export type { PlatformScanResult, PlatformScanOnceOptions };

export type PlatformCapabilities = {
  /** True inside the Capacitor Android/iOS shell. Browser and PWA stay false. */
  isNative: boolean;
  storage: {
    getItem(key: string): Promise<string | null>;
    setItem(key: string, value: string): Promise<void>;
    removeItem(key: string): Promise<void>;
  };
  network: {
    isOnline(): boolean;
    subscribe(onChange: (online: boolean) => void): () => void;
  };
  scanner: {
    /**
     * True when a one-shot barcode scan can run (web: getUserMedia + BarcodeDetector).
     * Native scanner replaces this in CAP-3.
     */
    isAvailable(): boolean;
    scanOnce(options: PlatformScanOnceOptions): Promise<PlatformScanResult>;
    /** Decode a barcode from a photo-library image. Does not open the camera. */
    scanImage(file: Blob): Promise<PlatformScanResult>;
  };
  camera: {
    isAvailable(): boolean;
  };
  printer: {
    isAvailable(): boolean;
  };
  push: {
    /** Browser returns `unsupported`. Native asks for permission and returns an FCM/APNs token. */
    register(): Promise<PushRegisterResult>;
    subscribeOpen(handler: (open: PushOpen) => void): () => void;
    takePendingOpen(): PushOpen | null;
  };
};

export type PushRegisterResult =
  | { status: "ok"; platform: "android" | "ios"; deviceToken: string }
  | { status: "denied" }
  | { status: "unsupported" }
  | { status: "error" };

export type PushOpen = {
  routeHint: string | null;
  eventType: string | null;
  entityType: string | null;
  entityId: string | null;
};

export function createBrowserPlatform(): PlatformCapabilities {
  return {
    isNative: false,
    storage: {
      getItem: async (key) => localStorage.getItem(key),
      setItem: async (key, value) => {
        localStorage.setItem(key, value);
      },
      removeItem: async (key) => {
        localStorage.removeItem(key);
      },
    },
    network: {
      isOnline: () =>
        typeof navigator === "undefined" ? true : navigator.onLine,
      subscribe: (onChange) => {
        const sync = () =>
          onChange(typeof navigator === "undefined" ? true : navigator.onLine);
        window.addEventListener("online", sync);
        window.addEventListener("offline", sync);
        return () => {
          window.removeEventListener("online", sync);
          window.removeEventListener("offline", sync);
        };
      },
    },
    scanner: {
      isAvailable: () => webScannerIsAvailable(),
      scanOnce: (options) => webScanOnce(options),
      scanImage: (file) => webScanImage(file),
    },
    camera: {
      isAvailable: () => webCameraIsAvailable(),
    },
    printer: { isAvailable: () => false },
    push: {
      register: async () => ({ status: "unsupported" }),
      subscribeOpen: () => () => {},
      takePendingOpen: () => null,
    },
  };
}

export const platform: PlatformCapabilities = createBrowserPlatform();

/** Swap capability impl in place so existing `platform` imports see the native shell. */
export function replacePlatform(next: PlatformCapabilities): void {
  platform.isNative = next.isNative;
  platform.storage = next.storage;
  platform.network = next.network;
  platform.scanner = next.scanner;
  platform.camera = next.camera;
  platform.printer = next.printer;
  platform.push = next.push;
}
